import { PrintTemplate, TEMPLATE_TYPE_LABELS } from "@/hooks/usePrintTemplates";
import { cn } from "@/lib/utils";

interface ReceiptPreviewProps {
  template: PrintTemplate;
  charsPerLine?: number;
}

export function ReceiptPreview({ template, charsPerLine = 48 }: ReceiptPreviewProps) {
  const templateName = TEMPLATE_TYPE_LABELS[template.template_type];
  const separator = template.separator_char.repeat(Math.floor(charsPerLine / 2));
  
  // Style helpers based on template settings
  const getTitleClasses = () => cn(
    "text-center",
    template.title_bold && "font-bold",
    template.title_size === "double" && "text-lg"
  );
  
  const getItemsClasses = () => cn(
    template.items_bold && "font-bold",
    template.items_size === "double" && "text-base"
  );
  
  const getTotalsClasses = () => cn(
    template.totals_bold && "font-bold",
    template.totals_size === "double" && "text-base"
  );

  // Sample data based on template type
  const getSampleContent = () => {
    switch (template.template_type) {
      case "order_receipt":
        return (
          <>
            <div className={getTitleClasses()}>
              <div>PEDIDO #1234</div>
              <div className="text-xs opacity-70">Mesa 05</div>
            </div>
            <div className="text-xs text-center opacity-60">
              {separator}
            </div>
            <div className={cn("space-y-1", getItemsClasses())}>
              <div className="flex justify-between">
                <span>2x Hamburguer Classic</span>
              </div>
              <div className="flex justify-between">
                <span>1x Batata Frita G</span>
              </div>
              <div className="flex justify-between">
                <span>2x Refrigerante</span>
              </div>
              <div className="text-xs opacity-70 pl-4">- Sem cebola</div>
            </div>
            <div className="text-xs text-center opacity-60">
              {separator}
            </div>
            <div className="text-xs text-center opacity-70">
              Garcom: Joao - 14:35
            </div>
          </>
        );
      
      case "customer_receipt":
        return (
          <>
            <div className={getTitleClasses()}>
              <div>VIA DO CLIENTE</div>
              <div className="text-xs opacity-70">Pedido #1234 - Mesa 05</div>
            </div>
            <div className="text-xs text-center opacity-60">
              {separator}
            </div>
            <div className={cn("space-y-0.5", getItemsClasses())}>
              <div className="flex justify-between">
                <span>2x Hamburguer Classic</span>
                <span>R$ 56,00</span>
              </div>
              <div className="flex justify-between">
                <span>1x Batata Frita G</span>
                <span>R$ 18,00</span>
              </div>
              <div className="flex justify-between">
                <span>2x Refrigerante</span>
                <span>R$ 12,00</span>
              </div>
            </div>
            <div className="text-xs text-center opacity-60">
              {separator}
            </div>
            <div className={cn("space-y-0.5", getTotalsClasses())}>
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span>R$ 86,00</span>
              </div>
              <div className="flex justify-between">
                <span>Taxa de Servico (10%):</span>
                <span>R$ 8,60</span>
              </div>
              <div className="flex justify-between text-base font-bold">
                <span>TOTAL:</span>
                <span>R$ 94,60</span>
              </div>
            </div>
          </>
        );
      
      case "conference_bill":
        return (
          <>
            <div className={getTitleClasses()}>
              <div>CONFERENCIA DE CONTA</div>
              <div className="text-xs opacity-70">Mesa 05</div>
            </div>
            <div className="text-xs text-center opacity-60">
              {separator}
            </div>
            <div className={cn("space-y-0.5", getItemsClasses())}>
              <div className="flex justify-between">
                <span>2x Hamburguer</span>
                <span>R$ 56,00</span>
              </div>
              <div className="flex justify-between">
                <span>1x Batata Frita</span>
                <span>R$ 18,00</span>
              </div>
              <div className="flex justify-between">
                <span>3x Chopp 500ml</span>
                <span>R$ 45,00</span>
              </div>
            </div>
            <div className="text-xs text-center opacity-60">
              {separator}
            </div>
            <div className={cn("space-y-0.5", getTotalsClasses())}>
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span>R$ 119,00</span>
              </div>
              <div className="flex justify-between">
                <span>Servico (10%):</span>
                <span>R$ 11,90</span>
              </div>
              <div className="flex justify-between font-bold">
                <span>TOTAL:</span>
                <span>R$ 130,90</span>
              </div>
            </div>
            <div className="text-xs text-center opacity-60">
              {separator}
            </div>
            <div className="text-xs text-center opacity-70">
              ** CONFERENCIA - NAO E DOCUMENTO FISCAL **
            </div>
          </>
        );
      
      case "cash_movement":
        return (
          <>
            <div className={getTitleClasses()}>
              <div>SANGRIA DE CAIXA</div>
            </div>
            <div className="text-xs text-center opacity-60">
              {separator}
            </div>
            <div className={cn("space-y-1 text-center", getTotalsClasses())}>
              <div className="text-xl font-bold">R$ 500,00</div>
              <div className="text-xs opacity-70">
                Motivo: Deposito bancario
              </div>
            </div>
            <div className="text-xs text-center opacity-60">
              {separator}
            </div>
            <div className="text-xs text-center opacity-70">
              Operador: Maria Silva
              <br />
              31/12/2024 15:30
            </div>
          </>
        );
      
      case "cash_close":
        return (
          <>
            <div className={getTitleClasses()}>
              <div>FECHAMENTO DE CAIXA</div>
            </div>
            <div className="text-xs text-center opacity-60">
              {separator}
            </div>
            <div className={cn("space-y-0.5", getTotalsClasses())}>
              <div className="flex justify-between">
                <span>Abertura:</span>
                <span>R$ 200,00</span>
              </div>
              <div className="flex justify-between">
                <span>Suprimentos:</span>
                <span>R$ 100,00</span>
              </div>
              <div className="flex justify-between">
                <span>Sangrias:</span>
                <span>-R$ 500,00</span>
              </div>
              <div className="flex justify-between">
                <span>Vendas Dinheiro:</span>
                <span>R$ 1.850,00</span>
              </div>
            </div>
            <div className="text-xs text-center opacity-60">
              {separator}
            </div>
            <div className={cn("space-y-0.5", getTotalsClasses())}>
              <div className="flex justify-between font-bold">
                <span>Esperado:</span>
                <span>R$ 1.650,00</span>
              </div>
              <div className="flex justify-between font-bold">
                <span>Contado:</span>
                <span>R$ 1.648,50</span>
              </div>
              <div className="flex justify-between text-destructive">
                <span>Diferenca:</span>
                <span>-R$ 1,50</span>
              </div>
            </div>
            <div className="text-xs text-center opacity-60">
              {separator}
            </div>
            <div className="text-xs text-center opacity-70">
              Operador: Maria Silva
            </div>
          </>
        );
      
      case "cancellation_receipt":
        return (
          <>
            <div className={getTitleClasses()}>
              <div>COMPROVANTE DE CANCELAMENTO</div>
              <div className="text-xs opacity-70">Mesa 05</div>
            </div>
            <div className="text-xs text-center opacity-60">
              {separator}
            </div>
            <div className={cn("space-y-1", getItemsClasses())}>
              <div className="font-bold">Picanha 300g</div>
              <div className="flex justify-between">
                <span>Qtd: 1</span>
                <span>R$ 89,00</span>
              </div>
              <div className="text-xs opacity-70">
                Motivo: Cliente desistiu
              </div>
            </div>
            <div className="text-xs text-center opacity-60">
              {separator}
            </div>
            <div className={getTotalsClasses()}>
              <div className="text-center text-base font-bold">
                VALOR: R$ 89,00
              </div>
            </div>
            <div className="text-xs text-center opacity-60">
              {separator}
            </div>
            <div className="text-xs text-center opacity-70">
              Cancelado por: Maria Santos
            </div>
            <div className="text-xs text-center opacity-60">
              {separator}
            </div>
            <div className="text-xs text-center">
              <div>RESPONSAVEL</div>
              <div className="my-2">_________________</div>
              <div className="opacity-70">Assinatura</div>
            </div>
          </>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="border rounded-lg bg-background p-4">
      <div className="text-xs font-medium text-muted-foreground mb-3 text-center uppercase tracking-wide">
        Preview do Recibo
      </div>
      
      {/* Receipt paper simulation */}
      <div className="mx-auto max-w-[280px] bg-white text-black rounded shadow-md overflow-hidden">
        {/* Paper top edge effect */}
        <div className="h-3 bg-gradient-to-b from-gray-200 to-white" />
        
        {/* Receipt content */}
        <div className="px-4 py-3 font-mono text-xs space-y-2">
          {/* Header text if configured */}
          {template.header_text && (
            <>
              <div className="text-center text-xs opacity-70">
                {template.header_text}
              </div>
              <div className="text-xs text-center opacity-60">
                {separator}
              </div>
            </>
          )}
          
          {/* Dynamic content based on template type */}
          {getSampleContent()}
          
          {/* Footer */}
          <div className="text-xs text-center opacity-60 pt-2">
            {separator}
          </div>
          
          {template.footer_text && (
            <div className="text-center text-xs opacity-70">
              {template.footer_text}
            </div>
          )}
          
          {template.show_software_footer && (
            <div className="text-center text-xs opacity-50">
              Altus Food
            </div>
          )}
        </div>
        
        {/* Paper bottom edge effect */}
        <div className="h-6 bg-gradient-to-t from-gray-100 to-white" 
             style={{ 
               maskImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 100 20\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M0 0 L5 10 L10 0 L15 10 L20 0 L25 10 L30 0 L35 10 L40 0 L45 10 L50 0 L55 10 L60 0 L65 10 L70 0 L75 10 L80 0 L85 10 L90 0 L95 10 L100 0 L100 20 L0 20 Z\'/%3E%3C/svg%3E")',
               WebkitMaskImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 100 20\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M0 0 L5 10 L10 0 L15 10 L20 0 L25 10 L30 0 L35 10 L40 0 L45 10 L50 0 L55 10 L60 0 L65 10 L70 0 L75 10 L80 0 L85 10 L90 0 L95 10 L100 0 L100 20 L0 20 Z\'/%3E%3C/svg%3E")'
             }} 
        />
      </div>
      
      {/* Legend */}
      <div className="mt-3 text-xs text-muted-foreground text-center">
        Simulação visual - a impressão real pode variar
      </div>
    </div>
  );
}
